import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// FIREBASE REMOVED - PostgreSQL kullanıyoruz artık
import ErrorBoundary from './components/ErrorBoundary';
import NetworkErrorHandler from './components/NetworkErrorHandler';
import { ToastProvider } from './components/ToastProvider';
import { QueryProvider } from './providers/QueryProvider';
import { AuthProvider, useAuth } from './AuthProvider';
import { NotificationProvider } from './NotificationProvider';
import cleanupScheduler from './utils/cleanupScheduler';
import { OfflineIndicator } from './utils/offlineManager';
import { applyStoredConsent } from './utils/consent';
import ConsentBanner from './components/ConsentBanner';

// Temel CSS'ler - Sadece gerekli olanlar
import './design-system/styles/globals.css';
import './design-system/themes/index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
/* 🌙 Karanlık tema güvenlik ağı — EN SONDA yüklenmeli.
   Sabit açık renk yazılmış yüzlerce kuralın karanlık karşılığını içerir.
   Otomatik üretilir: scratchpad/karanlik_uret.py */
import './design-system/styles/karanlik-tema-agi.css';
// 13.09.2026: renk geçişli (gradient) başlık yazıları düz başlığa çevrildi — ağdan sonra yüklenmeli.
import './design-system/styles/duz-baslik.css';
// Ağ, taban sınıflara !important yazdığı için seçili görünümleri siliyordu.
// Bu dosya ağdan SONRA gelmeli — sırası önemli.
import './design-system/styles/karanlik-secili-duzeltme.css';

import { Layout as DSLayout } from './design-system';
import { GlobalErrorBoundary, NetworkErrorBoundary } from "./ErrorBoundaries";

// Critical components - Load immediately
import SignIn from "./SignIn";
// 🔄 2026-08-29: ana sayfa koyu tema + Tailwind ile yeniden yazıldı.
// Eski sayfa `./LandingPage` DURUYOR — geri almak için aşağıdaki satırı
// `import LandingPage from "./LandingPage";` ile değiştirmek yeterli.
import LandingPage from "./pages/Landing/LandingPage";
import InitialRegister from "./InitialRegister";

// Import route modules
import MainRoutes from './routes/MainRoutes';
import PublicRoutes from './routes/PublicRoutes';

// Lazy loaded admin module
import { createLazyRoute } from './utils/lazyLoading';
const AdminModule = createLazyRoute(() => import('./admin/index'));
const RoleSignIn = createLazyRoute(() => import('./RoleSignIn'));
const EmailVerification = createLazyRoute(() => import('./EmailVerification'));
const SMSVerification = createLazyRoute(() => import('./SMSVerification'));

// Type definitions
interface LoadingScreenProps {
  message?: string;
}

// Geçici tip tanımlamaları - JavaScript dosyaları TypeScript'e dönüştürülene kadar
const SignInComponent: any = SignIn;

/**
 * Giriş yapılmamışken istenen adresi saklar ve ana sayfaya yönlendirir.
 * Girişten sonra `AuthProvider` bu adresi kullanır.
 *
 * Yalnız uygulama içi yollar saklanır: dışarıdan gelen "//baska-site" gibi
 * bir değer açık yönlendirme (open redirect) açığı olurdu.
 */
const HEDEF_ANAHTAR = 'girisSonrasiHedef';

const HedefiHatirla: React.FC = () => {
    const yer = useLocation();
    const tam = `${yer.pathname}${yer.search}`;
    const guvenli = tam.startsWith('/') && !tam.startsWith('//');
    if (guvenli && yer.pathname !== '/' && yer.pathname !== '/signin') {
        try { sessionStorage.setItem(HEDEF_ANAHTAR, tam); } catch { /* özel sekme */ }
    }
    return <Navigate to="/signin" replace />;
};
const LandingPageComponent: any = LandingPage;
const InitialRegisterComponent: any = InitialRegister;

const LoadingScreen: React.FC<LoadingScreenProps> = ({ message = "Oturum yükleniyor…" }) => (
    <DSLayout className="min-h-screen">
        <DSLayout.Flex
            direction="col"
            align="center"
            justify="center"
            className="min-h-screen bg-gradient-primary"
        >
            <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
                </div>
                <p className="text-white font-medium">{message}</p>
            </div>
        </DSLayout.Flex>
    </DSLayout>
);

export default function App(): React.ReactElement {
    // Initialize performance optimizations
    useEffect(() => {
        // Prod'da optimize et, dev'de geciktirme
        applyStoredConsent();
        // Cleanup on unmount
        return () => {
            cleanupScheduler.stop();
        };
    }, []);

    return (
        <ErrorBoundary>
            <NetworkErrorHandler>
                <QueryProvider>
                    <ToastProvider>
                        <AuthProvider>
                            <NotificationProvider>
                                <Router>
                                    <AppInner />
                                    <ConsentBanner />
                                </Router>
                            </NotificationProvider>
                        </AuthProvider>
                    </ToastProvider>
                </QueryProvider>
            </NetworkErrorHandler>
        </ErrorBoundary>
    );
}

function AppInner(): React.ReactElement {
    const { user, selectedRole, initializing } = useAuth();
    const [isLoadingCompanyData, setIsLoadingCompanyData] = useState<boolean>(true);
    const location = useLocation();

    // Theme initialization
    useEffect(() => {
        const initializeTheme = (): void => {
            const savedTheme = localStorage.getItem('theme');
            const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
            
            if (savedTheme) {
                document.documentElement.setAttribute('data-theme', savedTheme);
            } else {
                const theme = prefersDark ? 'dark' : 'light';
                document.documentElement.setAttribute('data-theme', theme);
                localStorage.setItem('theme', theme);
            }
        };

        initializeTheme();
    }, []);

    useEffect(() => {
        // PostgreSQL: User objesinde zaten company_id var, ayrıca yüklemeye gerek yok
        setIsLoadingCompanyData(false);
    }, [user]);

    // Track visitor on every page change
    useEffect(() => {
        // Skip tracking for admin pages
        if (location.pathname.startsWith('/admin')) {
            return;
        }
        
        // Track visitor
        const trackVisitor = async (): Promise<void> => {
            try {
                const apiUrl = process.env.NODE_ENV === 'production' 
                    ? `${window.location.origin}/api` 
                    : 'http://localhost:5001/api';
                
                // Get referer from document.referrer (where user came from)
                const referer = document.referrer || '';
                
                await fetch(`${apiUrl}/admin/visitor-stats/track`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        page_path: location.pathname,
                        referer: referer || null
                    })
                });
            } catch (error) {
                // Silently fail - don't break the page if tracking fails
            }
        };
        
        trackVisitor();
    }, [location.pathname]);

    // Track outbound link clicks (external links)
    useEffect(() => {
        const handleOutboundClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            const link = target.closest('a');
            
            if (!link || !link.href) return;
            
            try {
                const linkUrl = new URL(link.href);
                const currentUrl = new URL(window.location.href);
                
                // Only track external links (different domain)
                if (linkUrl.hostname !== currentUrl.hostname && !linkUrl.hostname.includes('tservis.tr')) {
                    const apiUrl = process.env.NODE_ENV === 'production' 
                        ? `${window.location.origin}/api` 
                        : 'http://localhost:5001/api';
                    
                    const trackingData = {
                        page_path: window.location.pathname,
                        outbound_url: link.href,
                        link_text: link.textContent?.substring(0, 100) || ''
                    };
                    
                    // Use fetch with keepalive for reliable tracking even when page is closing
                    fetch(`${apiUrl}/admin/visitor-stats/track-outbound`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(trackingData),
                        keepalive: true // Ensures request completes even if page unloads
                    }).catch(() => {
                        // Silently fail
                    });
                }
            } catch (error) {
                // Invalid URL, skip silently
            }
        };
        
        // Use capture phase to catch links before navigation
        document.addEventListener('click', handleOutboundClick, true);
        
        return () => {
            document.removeEventListener('click', handleOutboundClick, true);
        };
    }, []);

    if (initializing || isLoadingCompanyData) {
        return <LoadingScreen />;
    }

    return (
      <GlobalErrorBoundary>
        <NetworkErrorBoundary>
          <DSLayout className="min-h-screen bg-gray-50">
            <Routes>
                {/* Admin Routes - Super Admin Access */}
                <Route path="/admin/*" element={<AdminModule />} />
                
                {/* Public Routes - Always accessible - MUST be before wildcard routes */}
                <Route path="/" element={<LandingPageComponent />} />
                <Route path="/signin" element={<SignInComponent />} />
                <Route path="/register" element={<InitialRegisterComponent />} />
                <Route path="/verify-email/:token" element={<EmailVerification />} />
                <Route path="/verify-sms" element={<SMSVerification />} />
                
                {/* Public Routes Module - Must be before any wildcard routes */}
                {PublicRoutes}
                
                {/* User authenticated but no role selected - show role signin */}
                {user && !selectedRole && !user.email?.includes('admin') && (
                    <>
                        <Route path="/role-signin" element={<RoleSignIn />} />
                        <Route path="*" element={<Navigate to="/role-signin" replace />} />
                    </>
                )}

                {/* User authenticated with role - show main routes */}
                {user && selectedRole && !user.email?.includes('admin') && (
                    <Route path="/*" element={<MainRoutes />} />
                )}

                {/* Unauthenticated users - redirect to landing (but only after auth is initialized) */}
                {/* This must be LAST to not override public routes */}
                {!initializing && !user && (
                    /* 🔴 HEDEF ADRES KAYBOLUYORDU — 2026-08-30
                       WhatsApp'tan gönderilen saha servisi bağlantısı
                       (/service/saha-servis?is=...) giriş yapılmamış bir
                       telefonda açılınca doğrudan ana sayfaya atılıyor ve
                       teknisyen işi bulamıyordu. Adres saklanıyor; giriş
                       tamamlanınca oraya götürülüyor (bkz. HedefiHatirla). */
                    <Route path="*" element={<HedefiHatirla />} />
                )}
            </Routes>
          </DSLayout>
          <OfflineIndicator />
        </NetworkErrorBoundary>
      </GlobalErrorBoundary>
    );
}
